import { useEffect, type CSSProperties, type MouseEvent } from "react";
import { useRouter } from "@tanstack/react-router";
import { onlineManager } from "@tanstack/react-query";
import { registerOnlineCallback } from "@/lib/reachability";
// MA-160: the router-level error fallback. Before this component existed a route
// crash rendered TanStack Router's BUILT-IN "Something went wrong! [Show Error]"
// screen (that is exactly what prod showed during the 2026-07-10 MA-111 outage) —
// a surface WE don't control and the live-QA capture driver cannot recognize.
// This fallback (a) carries the `data-app-crashed` marker the driver detects
// (attribute-presence only — no kid content is ever read), and (b) shows the same
// calm, non-destructive recovery UI as ErrorBoundary (reload; never wipes storage).
export function RouterErrorFallback({ error }: { error: unknown }) {
  const msg = import.meta.env.DEV ? String((error as Error)?.message ?? error ?? "") : "";
  return (
    <div data-app-crashed="true" style={{
      display: "flex", minHeight: "100dvh", alignItems: "center",
      justifyContent: "center", padding: "32px 20px",
      fontFamily: "system-ui, sans-serif", color: "var(--ink, #2A1F1A)",
      textAlign: "center",
    }}>
      <div style={{ maxWidth: 360 }}>
        <div style={{ fontSize: 48, marginBottom: 8 }} aria-hidden="true">{"\u{1F422}"}</div>
        <h1 style={{ fontSize: 24, marginBottom: 10, fontWeight: 800 }}>Whoops! Let&apos;s try again</h1>
        <p style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 20, opacity: 0.85 }}>
          Something got stuck. Tap the button to pick up where you left off.
        </p>
        {import.meta.env.DEV && msg ? (
          <pre style={{ textAlign: "left", fontSize: 12, whiteSpace: "pre-wrap", marginBottom: 16 }}>{msg}</pre>
        ) : null}
        <button
          onClick={() => location.reload()}
          style={{
            padding: "12px 22px", borderRadius: 14, minHeight: 44,
            background: "var(--cta, #C76A48)", color: "#fff", border: "none",
            fontFamily: "system-ui", fontWeight: 700, fontSize: 16, cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

// ── MA-107: surface-aware, retry-in-place route fallbacks ───────────────────
//
// Route crashes render a calm card IN the Outlet (app chrome, OfflineBanner and
// tab bar stay up). The error components are imported EAGERLY (vite.config.js
// keeps `errorComponent` out of the autoCodeSplitting groups) so the fallback
// never depends on a network fetch. Sentry capture lives in router.ts
// (`defaultOnCatch`) — route crashes are caught by TanStack's CatchBoundary and
// never reach the root ErrorBoundary. Never wipes storage; never shows an error
// message/stack in production.
//
// Copy is deliberately short and fault-free: no "error", "failed", "crash",
// no consent/legal terms, no blame on the child.
export const ROUTE_ERROR_COPY = {
  parent: { line: "Something went wrong loading this page.", retry: "Try again", home: "Back to start" },
  kid: { retry: "Try again", home: "Home" },
} as const;

type RouterLike = { invalidate?: () => unknown; navigate?: (o: { to: string }) => unknown } | null | undefined;
type RouteErrorProps = { error: unknown; reset?: () => void };

/** A failed dynamic import (lazy route chunk). TanStack's lazyRouteComponent
 *  caches the rejected import in a closure and rethrows it on every render, so
 *  reset()/invalidate() can NEVER clear it — only a reload re-imports. Same
 *  heuristics as @tanstack/router-core isModuleNotFoundError, plus the webpack
 *  ChunkLoadError shape. */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: unknown; message?: unknown } | null | undefined;
  if (e?.name === "ChunkLoadError") return true;
  const m = typeof e?.message === "string" ? e.message : "";
  return (
    m.startsWith("Failed to fetch dynamically imported module") ||
    m.startsWith("error loading dynamically imported module") ||
    m.startsWith("Importing a module script failed") ||
    /^Loading (CSS )?chunk [^ ]+ failed/.test(m)
  );
}

export interface RetryDeps {
  isOnline: () => boolean;
  reload: () => void;
  /** Run `fn` once at the next verified-online transition; returns cancel. */
  onceOnline: (fn: () => void) => () => void;
}

let armedReload: (() => void) | null = null;

const defaultRetryDeps: RetryDeps = {
  isOnline: () => onlineManager.isOnline(),
  reload: () => location.reload(),
  onceOnline: (fn) => {
    const off = registerOnlineCallback(() => { off(); fn(); });
    return off;
  },
};

/** Retry in place. Chunk-load failures need a reload (online now, or armed for
 *  the next verified reconnect so we never reload INTO the browser's offline
 *  page — there is no service worker). Everything else: re-mount the route
 *  (CatchBoundary reset) and re-run its loaders. Each step is guarded so a
 *  failing one never blocks the other or throws out of the click handler. */
export function runRetry(
  router: RouterLike,
  reset?: () => void,
  error?: unknown,
  deps: RetryDeps = defaultRetryDeps,
): void {
  if (isChunkLoadError(error)) {
    try {
      if (deps.isOnline()) { deps.reload(); return; }
      if (!armedReload) {
        armedReload = deps.onceOnline(() => { armedReload = null; deps.reload(); });
      }
    } catch { /* */ }
    return;
  }
  try { reset?.(); } catch { /* */ }
  try { void router?.invalidate?.(); } catch { /* */ }
}

/** Cancel an armed reconnect reload (Home tapped, or the card unmounted) so a
 *  later reconnect cannot yank the child out of wherever they went. */
export function cancelArmedReload(): void {
  const off = armedReload;
  armedReload = null;
  try { off?.(); } catch { /* */ }
}

/** Test seam: forget an armed reload. */
export function __resetRetryState(): void { armedReload = null; }

/** The cards cancel any armed reload when they unmount (navigation away, reset). */
function useCancelArmedReloadOnUnmount(): void {
  useEffect(() => cancelArmedReload, []);
}

/** Leave the crashed route INSIDE the app (a full document load offline lands
 *  on the browser's "not connected" page — no service worker). `href` stays on
 *  the anchor only as the no-JS fallback. */
export function runGoHome(
  router: RouterLike,
  reset: (() => void) | undefined,
  to: string,
  e?: { preventDefault?: () => void } | null,
): void {
  try { e?.preventDefault?.(); } catch { /* */ }
  cancelArmedReload();
  try { reset?.(); } catch { /* */ }
  try {
    if (router?.navigate) void router.navigate({ to });
    else if (typeof location !== "undefined") location.assign(to);
  } catch { /* */ }
  // Same-route target (e.g. already on /play): navigation is a no-op, so also
  // re-run loaders the way Retry does.
  try { void router?.invalidate?.(); } catch { /* */ }
}

const retryButtonBase: CSSProperties = {
  background: "var(--cta, #C76A48)", color: "#fff", border: "none",
  fontFamily: "system-ui", fontWeight: 700, cursor: "pointer",
  touchAction: "manipulation",
};

/** Hook-free presentational parent card (executed by tests via its callbacks). */
export function ParentRouteErrorCard(
  { message, onRetry, onHome }: { message?: string; onRetry: () => void; onHome: (e: MouseEvent) => void },
) {
  return (
    <div data-app-crashed="true" data-route-error="parent" style={{
      display: "flex", minHeight: "60dvh", alignItems: "center",
      justifyContent: "center", padding: "32px 20px",
      fontFamily: "system-ui, sans-serif", color: "var(--ink, #2A1F1A)", textAlign: "center",
    }}>
      <div style={{ maxWidth: 360 }}>
        <p style={{ fontSize: 18, lineHeight: 1.5, marginBottom: 20, fontWeight: 600 }}>
          {ROUTE_ERROR_COPY.parent.line}
        </p>
        {message ? <pre style={{ textAlign: "left", fontSize: 12, whiteSpace: "pre-wrap", marginBottom: 16 }}>{message}</pre> : null}
        <button type="button" onClick={onRetry} style={{
          ...retryButtonBase, padding: "12px 22px", borderRadius: 14, minHeight: 44, fontSize: 16,
        }}>{ROUTE_ERROR_COPY.parent.retry}</button>
        <div style={{ marginTop: 14 }}>
          <a href="/" onClick={onHome} style={{ fontSize: 14, opacity: 0.8, color: "inherit" }}>{ROUTE_ERROR_COPY.parent.home}</a>
        </div>
      </div>
    </div>
  );
}

/** Hook-free presentational kid card: wordless-friendly — a big friendly icon
 *  and one large Try again button; a house-icon escape. */
export function KidRouteErrorCard(
  { onRetry, onHome }: { onRetry: () => void; onHome: (e: MouseEvent) => void },
) {
  return (
    <div data-app-crashed="true" data-route-error="kid" style={{
      display: "flex", flexDirection: "column", minHeight: "60dvh", alignItems: "center",
      justifyContent: "center", gap: 20, padding: "24px 20px",
      fontFamily: "system-ui, sans-serif", color: "var(--ink, #2A1F1A)", textAlign: "center",
    }}>
      <div style={{ fontSize: 72 }} aria-hidden="true">{"\u{1F422}"}</div>
      <button type="button" onClick={onRetry} style={{
        ...retryButtonBase, padding: "16px 36px", borderRadius: 20, minHeight: 64, fontSize: 24,
      }}>{ROUTE_ERROR_COPY.kid.retry}</button>
      <a href="/play" onClick={onHome} aria-label={ROUTE_ERROR_COPY.kid.home} style={{
        display: "grid", placeItems: "center", width: 56, height: 56, borderRadius: 28,
        border: "2px solid currentColor", fontSize: 26, textDecoration: "none", color: "inherit",
        opacity: 0.7, touchAction: "manipulation",
      }}><span aria-hidden="true">{"\u{1F3E0}"}</span></a>
    </div>
  );
}

/** Parent-facing (onboarding, dashboard, settings): one sentence + Try again. */
export function ParentRouteError({ error, reset }: RouteErrorProps) {
  const router = useRouter({ warn: false });
  useCancelArmedReloadOnUnmount();
  const message = import.meta.env?.DEV ? String((error as Error)?.message ?? error ?? "") : "";
  return (
    <ParentRouteErrorCard
      message={message}
      onRetry={() => runRetry(router, reset, error)}
      onHome={(e) => runGoHome(router, reset, "/", e)}
    />
  );
}

/** Kid-facing (play). */
export function KidRouteError({ error, reset }: RouteErrorProps) {
  const router = useRouter({ warn: false });
  useCancelArmedReloadOnUnmount();
  return (
    <KidRouteErrorCard
      onRetry={() => runRetry(router, reset, error)}
      onHome={(e) => runGoHome(router, reset, "/play", e)}
    />
  );
}

// Kid-facing surfaces: a pre-reader may be at the device. /onboarding/diagnostic
// is the kid's own check-in ("Pass the iPad to ..."); the other /onboarding/kid/*
// steps are parent-entered (name, age, abilities, locale) and keep the parent card.
const KID_PREFIXES = ["/play", "/journey", "/library", "/read", "/pal", "/mascot-pick", "/done", "/session", "/concept", "/onboarding/diagnostic", "/onboarding/kid/pal"];

/** Which fallback a path gets: kid-facing routes get the wordless-friendly
 *  card, everything else (onboarding, /parent/*, settings, ...) the parent one. */
export function surfaceForPath(pathname: string): "kid" | "parent" {
  const p = String(pathname || "");
  return KID_PREFIXES.some((k) => p === k || p.startsWith(k + "/")) ? "kid" : "parent";
}

/** The router's defaultErrorComponent for every non-root route (eager, so it is
 *  always in the main bundle). `pathname` is injectable for tests. */
export function SurfaceRouteError(props: RouteErrorProps & { pathname?: string }) {
  const pathname = props.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  return surfaceForPath(pathname) === "kid"
    ? <KidRouteError error={props.error} reset={props.reset} />
    : <ParentRouteError error={props.error} reset={props.reset} />;
}
